import { ChangeDetectorRef, Component, effect, ElementRef, inject, input, OnInit, QueryList, signal, ViewChild, ViewChildren, WritableSignal } from '@angular/core';
import { AbstractControl, ControlContainer, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TextFilterPipe } from '../../pipes/text-filter.pipe';

@Component({
  selector: 'terra-custom-properties',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    TextFilterPipe
  ],
  styleUrl: './custom-properties.component.scss',
  templateUrl: './custom-properties.component.html',
})
export class CustomPropertiesComponent implements OnInit  {
  @ViewChildren('properties') properties: QueryList<ElementRef>;
  @ViewChild('addButton') addButton: MatButton;

  private readonly controlContainer: ControlContainer = inject(ControlContainer, { skipSelf: true });
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly cd: ChangeDetectorRef = inject(ChangeDetectorRef);
 
  readonly suggestions = input<string[]>([]);
  readonly editStates: WritableSignal<Map<number, { isEditing: boolean; originalValue: { name: string; value: string } | null }>> = signal(new Map());

  readonly formArrayName = input.required<string>();

  readonly disabled = signal(false);

  get formArray(): FormArray {
    return this.controlContainer.control?.get(this.formArrayName()) as FormArray;
  }

  get existingNames(): Array<string> {
    return this.formArray?.controls?.map((control) => control.value.name) || [];
  }

  constructor() {
    effect(() => {
      const list = this.formArray?.value || [];
      this.formArray?.clear();
      list.forEach((item) => this.formArray?.push(this.createRow(item)));

      this.formArray?.updateValueAndValidity();
      this.cd.detectChanges();
    });
}

  ngOnInit(): void {
    if (!this.formArray) {
      throw new Error(`FormArray with name '${this.formArrayName()}' was not found in the parent form.`);
    }

    this.formArray.valueChanges.subscribe(() => {
      this.disabled.set(this.formArray.length >= 10);
    });
  }

  addRow(): void {
    if (!this.formArray) return;
    this.formArray.push(this.createRow());
    const position = this.formArray.length - 1;
    this.editStates.update((state) => {
      state.set(position, { isEditing: true, originalValue: null });
      return new Map(state);
    });
    this.focusOnInput(position);
  }

  removeRow(index: number): void {
    this.formArray.removeAt(index);

    this.returnFocus();
  }

  saveRow(index: number): void {
    this.editStates.update((state) => {
      state.set(index, { isEditing: false, originalValue: null });
      return new Map(state);
    });

    this.returnFocus();
  }

  enableEdit(index: number): void {
    this.editStates.update((state) => {
      state.set(index, { isEditing: true, originalValue: { ...this.formArray.at(index)?.value } });
      return new Map(state);
    });

    this.focusOnInput(index);
  }

  cancelEdit(index: number): void {
    if (!this.editStates().has(index)) return;
    const state = this.editStates().get(index)!;

    if (state.originalValue === null) {
      // If it's a new row, remove it
      this.removeRow(index);
    } else {
      // Restore the original value for existing rows
      this.formArray.at(index).patchValue(state.originalValue);
      state.isEditing = false;
    }

    this.returnFocus();
  }

  private createRow(data: {name: string, value: string, id?: number} = { name: '', value: '' }): FormGroup {
    return this.fb.group({
      name: [data.name, [Validators.required, Validators.maxLength(50), this.duplicateValidation()]],
      value: [data.value,[Validators.required, Validators.maxLength(1000)] ],
      id: [data.id]
    });
  }


  private duplicateValidation(): ValidatorFn {
    // should check if name is already in the list but ignores self
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) {
        return null;
      }
      
      const row = control?.parent as FormGroup;
      const list = row?.parent as FormArray;
      const existingNames: Array<string> = [...(list?.controls || [])].filter((control) => control !== row).map((control) => control.value.name);

      return existingNames.includes(control.value as string) ? {exist: true} : null;
    };
    
  }

  private focusOnInput(index: number): void {
    // focus on element after it is rendered
    setTimeout(() => {
      this.properties.toArray()[index]?.nativeElement?.querySelector('input')?.focus();
    }, 50);
  } 

  private returnFocus(): void {
    this.addButton?.focus();
  }

}
